import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, X, AlertTriangle, Send } from "lucide-react";
import { http, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRealtime } from "../context/RealtimeContext";

const Names = ({ items, testid, tone }) => (
  <ul className={`max-h-40 overflow-y-auto text-xs divide-y divide-slate-100 border rounded ${tone}`} data-testid={testid}>
    {items.map((a) => <li key={a.id} className="px-2 py-1 flex justify-between gap-2"><span className="font-semibold">{a.name}</span>{a.email && <span className="text-slate-500 truncate">{a.email}</span>}{a.reason && <span className="text-rose-700 truncate">{a.reason}</span>}</li>)}
  </ul>
);

const Result = ({ job }) => (
  <div className="space-y-2 text-sm" data-testid="cred-result">
    <p className="font-semibold" data-testid="cred-progress">{job.status === "running" ? "Envoi en cours…" : "Envoi terminé"} · {job.sent} / {job.total} e-mail(s) envoyé(s)</p>
    {job.error && <p className="text-rose-700 text-xs" data-testid="cred-job-error">{job.error}</p>}
    {job.failed.length > 0 && <><p className="text-rose-700 font-semibold">Échecs ({job.failed.length}) — PIN inchangé, gardés en alerte :</p><Names items={job.failed} testid="cred-failed" tone="border-rose-200" /></>}
    {job.missing.length > 0 && <p className="text-amber-700 text-xs" data-testid="cred-missing-note">{job.missing.length} agent(s) sans e-mail gardé(s) en alerte : renvoi possible dès que l'adresse est complétée.</p>}
  </div>
);

export const CredentialsDialog = ({ initial, onClose }) => {
  const { user } = useAuth();
  const admin = user?.role === "admin";
  const [scope, setScope] = useState(initial?.scope || (admin ? "all" : "my_sites"));
  const [siteId, setSiteId] = useState("");
  const [sites, setSites] = useState([]);
  const [prev, setPrev] = useState(null);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [tick, setTick] = useState(0);
  const body = { scope, ...(scope === "site" ? { site_id: siteId } : {}), ...(scope === "agent" ? { agent_ids: initial.agent_ids } : {}) };
  useEffect(() => { http.get("/sites").then((r) => setSites(r.data.filter((s) => !s.archived))).catch((e) => toast.error(`Sites indisponibles : ${formatApiError(e)}`)); }, []);
  useEffect(() => {
    if (scope === "site" && !siteId) { setPrev(null); return; }
    let live = true;
    setPrev(null); setErr("");
    http.post("/agents/credentials/preview", body).then((r) => live && setPrev(r.data)).catch((e) => live && setErr(formatApiError(e)));
    return () => { live = false; };
  }, [scope, siteId, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!job || job.status !== "running") return;
    const t = setTimeout(() => http.get(`/agents/credentials/jobs/${job.id}`).then((r) => setJob(r.data)).catch(() => setJob((j) => ({ ...j }))), 1500);
    return () => clearTimeout(t);
  }, [job]);
  const send = async () => {
    setBusy(true);
    try { setJob((await http.post("/agents/credentials/send", body)).data); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const n = prev?.recipients.length || 0;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4" data-testid="cred-dialog">
      <div className="bg-white rounded-lg w-full max-w-xl p-5 space-y-4">
        <div className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-[#C9962E]" /><h2 className="font-display font-bold text-lg">Envoyer les identifiants de l'application</h2>
          <button type="button" onClick={onClose} className="ml-auto p-1 rounded hover:bg-slate-100" data-testid="cred-close"><X className="h-4 w-4" /></button></div>
        {!job && <>
          {scope !== "agent" && (
            <div className="flex flex-wrap gap-2 items-center">
              <select value={scope} onChange={(e) => setScope(e.target.value)} className="border border-slate-300 rounded-md px-2 py-1.5 text-sm" data-testid="cred-scope">
                {admin && <option value="all">Tous les agents actifs</option>}
                <option value="my_sites">Intervenants de mes sites</option>
                <option value="site">Un site</option>
              </select>
              {scope === "site" && <select value={siteId} onChange={(e) => setSiteId(e.target.value)} className="border border-slate-300 rounded-md px-2 py-1.5 text-sm min-w-[200px]" data-testid="cred-site">
                <option value="">Choisir un site…</option>{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>}
            </div>
          )}
          <p className="text-xs text-slate-600">Chaque agent reçoit par e-mail le lien de connexion, le code société, son identifiant et un <b>nouveau code PIN</b>. L'ancien PIN ne fonctionne plus et l'agent est déconnecté de son téléphone.</p>
          {err ? <p className="text-sm text-rose-700" data-testid="cred-preview-error">{err} <button type="button" onClick={() => setTick(tick + 1)} className="underline font-semibold" data-testid="cred-preview-retry">Réessayer</button></p>
            : !prev ? <p className="text-sm text-slate-500" data-testid="cred-preview-loading">{scope === "site" && !siteId ? "Choisissez un site." : "Chargement…"}</p> : <>
            <p className="text-sm font-semibold" data-testid="cred-recipients-count">{n} destinataire(s)</p>
            {n > 0 && <Names items={prev.recipients} testid="cred-recipients" tone="border-slate-200" />}
            {prev.missing.length > 0 && <div className="space-y-1"><p className="text-xs font-semibold text-amber-700 flex items-center gap-1" data-testid="cred-missing-count"><AlertTriangle className="h-3.5 w-3.5" />{prev.missing.length} agent(s) sans e-mail : non envoyés, gardés en alerte</p>
              <Names items={prev.missing} testid="cred-missing" tone="border-amber-200 bg-amber-50/40" /></div>}
          </>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-3 py-2 text-sm rounded-md border border-slate-300">Annuler</button>
            <button type="button" onClick={send} disabled={busy || !prev || (!n && !prev.missing.length)} data-testid="cred-send" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md bg-[#0B2A7A] text-white disabled:opacity-50"><Send className="h-4 w-4" />Envoyer ({n})</button>
          </div>
        </>}
        {job && <><Result job={job} /><div className="flex justify-end"><button type="button" onClick={onClose} className="px-3 py-2 text-sm rounded-md border border-slate-300" data-testid="cred-done">Fermer</button></div></>}
      </div>
    </div>
  );
};

export const PendingCredentials = ({ onSend }) => {
  const { lastEvent } = useRealtime();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState(false);
  const load = useCallback(() => { http.get("/agents/credentials/pending").then((r) => { setRows(r.data); setErr(false); }).catch(() => setErr(true)); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (["credentials_changed", "agent_changed"].includes(lastEvent?.event)) load(); }, [lastEvent, load]);
  if (err) return <p className="mb-5 text-xs text-rose-700" data-testid="cred-pending-error">Alertes d'identifiants indisponibles. <button type="button" onClick={load} className="underline font-semibold" data-testid="cred-pending-retry">Réessayer</button></p>;
  if (!rows.length) return null;
  return (
    <div className="mb-5 border border-amber-300 bg-amber-50 rounded-md px-3 py-2 text-sm" data-testid="cred-pending">
      <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-2 font-semibold text-amber-800" data-testid="cred-pending-toggle">
        <AlertTriangle className="h-4 w-4" />{rows.length} agent(s) en attente de leurs identifiants{open ? "" : " — voir"}</button>
      {open && <ul className="mt-2 divide-y divide-amber-200">
        {rows.map((r) => (
          <li key={r.agent_id} className="py-1.5 flex flex-wrap items-center gap-3" data-testid={`cred-pending-${r.agent_id}`}>
            <span className="font-semibold">{r.agent_name}</span>
            <span className="text-xs text-amber-800">{r.reason === "no_email" ? (r.email ? "E-mail complété : prêt à renvoyer" : "Adresse e-mail manquante") : `Échec d'envoi : ${r.detail || ""}`}</span>
            <span className="text-[11px] text-slate-500">{new Date(r.at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}{r.by ? ` · ${r.by}` : ""}</span>
            <button type="button" disabled={!r.email} onClick={() => onSend([r.agent_id])} className="ml-auto text-xs font-semibold text-[#0B2A7A] underline disabled:opacity-40 disabled:no-underline" data-testid={`cred-resend-${r.agent_id}`}>Renvoyer</button>
          </li>
        ))}
      </ul>}
    </div>
  );
};
